<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $t('messages.absenteeism'),
                        href: null,
                    },
                    {
                        name: $t('messages.years', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-5">
                    <FormLabel :value="$t('messages.grade_level')" for="grade_level_ids" />
                    <div class="mt-1">
                        <SlideOverCheckboxInput v-model="form.grade_level_ids" :options="options.grade_levels" />
                        <FormError :value="form.errors.grade_level_ids" class="mt-2" />
                    </div>
                </div>
                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-2 flex gap-x-2 items-center">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />

                        <AppButton element="a" :href="printUrl">
                            <IconPrinter class="-ml-0.5 mr-2 w-4 h-4" />
                            {{ $t('messages.print') }}
                        </AppButton>
                    </div>
                </div>
            </div>
        </form>
        <div v-if="table.years.length === 0" class="flex flex-col mt-5">
            <EmptyState picture />
        </div>
        <div class="flex flex-col mt-5" v-else>
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-2 py-1 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                ></th>
                                <th
                                    class="max-w-xs px-2 py-1 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    v-for="(period, periodIndex) in table.periods"
                                    :class="periodIndex % 2 === 0 ? 'bg-gray-200' : 'bg-white'"
                                    :key="period.id"
                                    colspan="3"
                                >
                                    {{ period.name }}
                                </th>
                                <th
                                    class="max-w-xs px-2 py-1 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    :class="table.periods.length % 2 === 0 ? 'bg-gray-200' : 'bg-white'"
                                    colspan="3"
                                >
                                    {{ $t('messages.trimester') }}
                                </th>
                            </tr>
                            <tr class="text-xs">
                                <th
                                    class="max-w-xs px-2 py-1 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                ></th>
                                <template v-for="(period, periodIndex) in table.periods.length + 1" :key="period.id">
                                    <th
                                        class="max-w-xs px-1 py-1 text-right text-xs leading-4 text-gray-500 font-medium"
                                        :class="periodIndex % 2 === 0 ? 'bg-gray-200' : 'bg-white'"
                                    >
                                        {{ $t('messages.number_of_students') }}
                                    </th>
                                    <th
                                        class="max-w-xs px-1 py-1 text-right text-xs leading-4 text-gray-500 font-medium"
                                        :class="periodIndex % 2 === 0 ? 'bg-gray-200' : 'bg-white'"
                                    >
                                        {{ $t('messages.attendance_average') }}
                                    </th>
                                    <th
                                        class="max-w-xs px-1 py-1 text-right text-xs leading-4 text-gray-500 font-medium"
                                        :class="periodIndex % 2 === 0 ? 'bg-gray-200' : 'bg-white'"
                                    >
                                        {{ $t('messages.number_of_absentee_students') }}
                                    </th>
                                </template>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(year, index) in table.years"
                                :key="year.school_year.id"
                            >
                                <td class="px-2 py-1 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ year.school_year.year_range }}
                                </td>
                                <template v-for="month in year.data" :key="month.month">
                                    <td class="px-2 py-1 whitespace-nowrap text-sm leading-5 text-gray-500 text-right">
                                        {{ month.count }}
                                    </td>
                                    <td class="px-2 py-1 whitespace-nowrap text-sm leading-5 text-gray-500 text-right">
                                        <span class="font-mono">{{ month.avg }}%</span>
                                    </td>
                                    <td class="px-2 py-1 whitespace-nowrap text-sm leading-5 text-gray-500 text-right">
                                        {{ month.absenteeism_count }}
                                    </td>
                                </template>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import EmptyState from '@/components/EmptyState.vue';
import SlideOverCheckboxInput from '@/components/Forms/SlideOverCheckboxInput.vue';
import { IconPrinter } from '@tabler/icons-vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        SlideOverCheckboxInput,
        PanelPage,
        AppButton,
        FormLabel,
        FormError,
        EmptyState,
        IconPrinter,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'filters', 'options', 'table'],
    data() {
        return {
            form: this.$inertia.form({
                grade_level_ids: this.filters.grade_level_ids,
                percent: this.filters.percent,
                trimester: this.filters.trimester,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
    computed: {
        printUrl: function () {
            let url = new URL(this.meta.links.index);

            this.filters.grade_level_ids.forEach((gl) => {
                url.searchParams.append('grade_level_ids[]', gl);
            });
            url.searchParams.append('percent', this.filters.percent);
            url.searchParams.append('trimester', this.filters.trimester);
            url.searchParams.append('wants_pdf', '1');

            return url.href;
        },
    },
};
</script>
